<script setup lang="ts">
/**
 * Module: web/src/views/ResetRequestView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Self-service password reset request (doc 04 sect 8.6). POST /auth/reset-request
 * always answers 202 (no user-enumeration signal) -- the confirmation copy is
 * deliberately the same whether or not the email exists.
 */
import { ref } from 'vue';
import { apiPost } from '@/composables/useApi';
import AppIcon from '@/components/AppIcon.vue';

const email = ref('');
const submitting = ref(false);
const sent = ref(false);
const errorMessage = ref('');

async function submit() {
  if (submitting.value) return;
  errorMessage.value = '';
  if (!email.value.trim()) {
    errorMessage.value = 'Enter your email address.';
    return;
  }
  submitting.value = true;
  try {
    await apiPost<void>('/auth/reset-request', { email: email.value.trim() });
    sent.value = true;
  } catch {
    errorMessage.value = 'Request failed. Check your connection and try again.';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-center px-4 py-8">
    <div class="w-full max-w-sm">
      <h1 class="text-xl font-bold text-fg mb-1">Forgot password</h1>
      <p class="text-sm text-fg-muted mb-6">
        Enter your account email. If it matches an active account, a reset link will be sent.
      </p>

      <div v-if="sent" class="space-y-5">
        <div class="alert-success flex items-start gap-2.5" role="status">
          <AppIcon name="check" class="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>If that email matches an account, a reset link is on its way. Check your inbox.</span>
        </div>
        <RouterLink to="/login" class="btn-secondary w-full">Back to sign in</RouterLink>
      </div>

      <form v-else class="space-y-4" novalidate @submit.prevent="submit">
        <div v-if="errorMessage" class="alert-error" role="alert">{{ errorMessage }}</div>
        <div>
          <label class="field-label" for="rr-email">Email</label>
          <input
            id="rr-email"
            v-model="email"
            class="field-input"
            type="email"
            autocomplete="username"
            inputmode="email"
            autocapitalize="none"
            spellcheck="false"
            :disabled="submitting"
          />
        </div>
        <button type="submit" class="btn-primary w-full" :disabled="submitting">
          {{ submitting ? 'Sending…' : 'Send reset link' }}
        </button>
        <div class="text-center">
          <RouterLink to="/login" class="inline-block py-2.5 px-3 text-sm text-accent hover:underline">
            Back to sign in
          </RouterLink>
        </div>
      </form>
    </div>
  </div>
</template>
